import React, { useEffect } from "react";

import {
  useDataGrid,
  DataGrid,
  GridColumns,
  List,
  Stack,
  esES,
  DateField,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  InputAdornment,
  Button,
  Avatar,
} from "@pankod/refine-mui";
import {
  CrudFilters,
  getDefaultFilter,
  HttpError,
  useModal,
} from "@pankod/refine-core";
import {
  InsertDriveFile,
  LockOpenOutlined,
  PictureAsPdf,
  SearchOutlined,
} from "@mui/icons-material";
import { IOrder, IOrderFilterVariables, IProduct, Nullable } from "interfaces";
import { EditButton as EditButtonCustom } from "components/buttons/edit";
import { OrderOpenModal } from "components/Modals/order_open";
import { ModalRender } from "../open/list";
import { Link } from "@pankod/refine-react-router-v6";
import { OrderDetailsModal } from "components/Modals/order_details";
import { useForm } from "@pankod/refine-react-hook-form";
import { LightboxCustom } from "components/LightboxCustom";
import { getImage, getMediaUrl } from "functions";
import { useIsReadOnly } from "hooks/useIsReadOnly";

export const OrderFinishedList: React.FC = () => {
  const { isReadOnly } = useIsReadOnly();
  const { dataGridProps, filters, setFilters } = useDataGrid<IProduct>({
    resource: "orders",
    permanentFilter: [
      { field: "status", value: ["Terminada", "Cancelada"], operator: "in" },
    ],
    metaData: {
      populate: {
        client: true,
        catalog: true,
        order_file: true,
        enabled: { populate: ["employee"] },
        pasted: { populate: ["employee"] },
        fretwork: { populate: ["employee"] },
        welded: { populate: ["employee"] },
        polish: { populate: ["employee"] },
        engrave: { populate: ["employee"] },
        detail: { populate: ["employee"] },
        assemble: { populate: ["employee"] },
        luster: { populate: ["employee"] },
        painted: { populate: ["employee"] },
        wrapped: { populate: ["employee", "files"] },
        sent: {
          populate: {
            sent_invoices: {
              populate: {
                invoice: {
                  populate: ["pdf", "xml"],
                },
              },
            },
          },
        },
      },
    },
    queryOptions: {
      cacheTime: 0,
    },
  });

  const { show, close, visible } = useModal();

  const defaultPreview = {
    images: [] as string[],
    index: 0,
    isOpen: false,
  };
  const [previewImage, setPreviewImage] = React.useState(defaultPreview);

  const [currentModal, setCurrentModal] = React.useState<any>(null);

  const handleModal = ({
    modal,
    title,
    data = {},
    order,
  }: {
    modal: string;
    title: string;
    data?: any;
    order: any;
  }) => {
    show();
    const params = {
      close: close,
      canAdd: true,
    };

    let currentData: any;

    switch (modal) {
      case "OrderOpenModal":
        currentData = {
          ...data,
          order: order,
        };

        setCurrentModal({
          ...params,
          modalComponent: OrderOpenModal,
          title: title,
          data: currentData,
        });
        break;

      case "OrderDetailsModal":
        currentData = {
          ...data,
          order: order,
        };

        setCurrentModal({
          ...params,
          modalComponent: OrderDetailsModal,
          title: title,
          data: currentData,
        });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setCurrentModal({
      ...currentModal,
      visible: visible,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const columns = React.useMemo<GridColumns<IOrder>>(
    () => [
      {
        field: "createdAt",
        headerName: "Fecha",
        flex: 1,
        headerClassName: "stycky-header",
        minWidth: 130,
        renderCell: function render({ row }) {
          return (
            <DateField
              value={row.createdAt}
              format={"DD / MMM / YYYY"}
              locales={"es-mx"}
              style={{ textTransform: "uppercase" }}
            />
          );
        },
      },
      { field: "type", headerName: "Tipo de Orden", minWidth: 130, flex: 1 },
      {
        field: "order",
        headerName: "# de Orden",
        minWidth: 130,
        flex: 1,
        renderCell: function render({ row }) {
          row = {
            ...row,
            norder: row.order,
          };

          return (
            <Link
              to="#"
              onClick={() => {
                handleModal({
                  modal: "OrderDetailsModal",
                  title: "Detalles de la orden",
                  data: row,
                  order: row?.id,
                });
              }}
            >
              {row.order}
            </Link>
          );
        },
      },
      {
        field: "order_file",
        headerName: "Pdf",
        minWidth: 50,
        flex: 1,
        align: "center",
        renderCell: (params) => {
          if (!params?.row?.order_file?.url) {
            return null;
          }

          return (
            <a
              href={getMediaUrl(params.row.order_file.url)}
              target="_blank"
              rel="noreferrer"
            >
              <PictureAsPdf fontSize="medium" style={{ color: "black" }} />
            </a>
          );
        },
      },
      {
        field: "sku",
        headerName: "SKU",
        minWidth: 130,
        flex: 1,
        valueGetter: (params) => {
          const catalog = params.row.catalog;
          if (Array.isArray(catalog)) {
            return catalog[0]?.sku || "";
          }
          return catalog?.sku || "";
        },
        renderCell: (params) => {
          return params.value || "---";
        },
      },
      {
        field: "sent_invoices_invoices",
        headerName: "Facturas",
        minWidth: 50,
        flex: 1,
        align: "center",
        renderCell: (params) => {
          const invoices = params?.row?.sent?.sent_invoices;
          if (!invoices?.length) {
            return "---";
          }
          return (
            <div>
              {invoices.map((invoice: any) => {
                return (
                  <div
                    key={invoice?.invoice?.id}
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    {invoice?.invoice?.invoice}

                    {invoice?.invoice?.pdf && (
                      <a
                        href={getMediaUrl(invoice.invoice.pdf?.url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <PictureAsPdf
                          fontSize="medium"
                          style={{ color: "black" }}
                        />
                      </a>
                    )}

                    {invoice?.invoice?.xml && (
                      <a
                        href={getMediaUrl(invoice.invoice.xml?.url)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <InsertDriveFile
                          fontSize="medium"
                          style={{ color: "black" }}
                        />
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          );
        },
      },
      {
        field: "wrapped_files",
        headerName: "Foto terminado",
        minWidth: 120,
        flex: 1,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const wrappedFiles = row?.wrapped?.files;
          const files = Array.isArray(wrappedFiles)
            ? wrappedFiles
            : wrappedFiles
            ? [wrappedFiles]
            : [];
          const images = files.filter(
            (file: any) => file?.ext !== ".pdf" && file?.url
          );

          if (!images.length) {
            return "---";
          }

          return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {images.slice(0, 2).map((file: any, index: number) => {
                const thumb = getMediaUrl(
                  file?.formats ? getImage(file, "small") : file.url
                );
                return (
                  <Avatar
                    key={file.id || index}
                    alt={file.name || "Foto terminado"}
                    src={thumb}
                    style={{ cursor: "pointer" }}
                    onClick={() =>
                      setPreviewImage({
                        images: images.map((image: any) =>
                          getMediaUrl(image.url)
                        ),
                        index,
                        isOpen: true,
                      })
                    }
                  />
                );
              })}
              {images.length > 2 && (
                <span style={{ fontSize: 12 }}>+{images.length - 2}</span>
              )}
            </Stack>
          );
        },
      },
      {
        field: "client",
        headerName: "Cliente",
        minWidth: 130,
        flex: 1,
        renderCell: (params) => {
          return params.row.client.name;
        },
      },
      {
        field: "final_client",
        headerName: "Cliente final",
        minWidth: 130,
        flex: 1,
      },
      {
        field: "delivery_date",
        headerName: "Fecha de entrega",
        minWidth: 130,
        flex: 1,
        renderCell: function render({ row }) {
          if (!row.delivery_date) return "Sin fecha";
          return (
            <DateField
              value={row.delivery_date}
              format={"DD / MMM / YYYY"}
              locales={"es-mx"}
              style={{ textTransform: "uppercase" }}
            />
          );
        },
      },
      // {
      //   field: "pdf",
      //   headerName: "PDF",
      //   flex: 1,
      //   renderCell: function render({ row }) {
      //     return "";
      //   },
      // },
      {
        field: "status",
        headerName: "Status",
        flex: 1,
        minWidth: 100,
      },
      ...(isReadOnly
        ? []
        : [
            {
              headerName: "Acciones",
              headerAlign: "center" as const,
              field: "actions",
              minWidth: 100,
              align: "center" as const,
              flex: 1,
              renderCell: function render(params: any) {
                const { row } = params;
                return (
                  <Stack direction="row" spacing={1}>
                    <EditButtonCustom
                      hideText
                      title={"Abrir orden"}
                      recordItemId={row.id}
                      buttonProps={{
                        variant: "contained",
                        size: "small",
                      }}
                      Icon={LockOpenOutlined}
                      style={{ color: "white" }}
                      onClick={() => {
                        handleModal({
                          modal: "OrderOpenModal",
                          title: "Abrir orden",
                          data: {
                            comments: row.comments,
                          },
                          order: row?.id,
                        });
                      }}
                    />
                  </Stack>
                );
              },
            },
          ]),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, isReadOnly]
  );

  const { register, handleSubmit, reset } = useForm<
    IOrder,
    HttpError,
    Nullable<IOrderFilterVariables>
  >({
    defaultValues: {
      q: getDefaultFilter("q", filters, "eq"),
    },
  });

  const handleSearch = (params: any) => {
    const { q } = params;
    const filters: CrudFilters = [];

    filters.push({
      operator: "or",
      value: [
        {
          field: "type",
          operator: "contains",
          value: q,
        },
        {
          field: "order",
          operator: "contains",
          value: q,
        },
        {
          field: "catalog.sku",
          operator: "contains",
          value: q,
        },
        {
          field: "client.name",
          operator: "contains",
          value: q,
        },
        {
          field: "final_client",
          operator: "contains",
          value: q,
        },
        {
          field: "status",
          operator: "contains",
          value: q,
        },
        {
          field: "sent.sent_invoices.invoice.invoice",
          operator: "contains",
          value: q,
        },
      ],
    });
    setFilters(filters);
  };

  return (
    <List>
      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ paddingX: { xs: 2, md: 0 } }}>
            <CardContent sx={{ pt: 0 }}>
              <Box
                component="form"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 2,
                  alignItems: "center",
                }}
                autoComplete="off"
                onSubmit={handleSubmit(handleSearch)}
              >
                <TextField
                  {...register("q")}
                  id="q"
                  label="Buscar"
                  placeholder="Tipo de Orden, # de Orden, SKU, cliente, cliente final, Status, Factura"
                  margin="normal"
                  fullWidth
                  autoFocus
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  style={{
                    height: 40,
                  }}
                >
                  Buscar
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  size="large"
                  color="error"
                  style={{
                    height: 40,
                  }}
                  onClick={() => {
                    setFilters([]);
                    reset();
                  }}
                >
                  Limpiar
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={12}>
          <DataGrid
            {...dataGridProps}
            autoHeight
            localeText={esES.components.MuiDataGrid.defaultProps.localeText}
            columns={columns}
            getRowHeight={() => "auto"}
            sx={{
              "& .MuiDataGrid-columnHeaderTitle": {
                textOverflow: "clip",
                whiteSpace: "break-spaces",
                lineHeight: 1,
              },
            }}
          />
        </Grid>
      </Grid>
      <LightboxCustom
        open={previewImage.isOpen}
        onClose={() => setPreviewImage(defaultPreview)}
        files={previewImage.images.map((src) => ({
          url: src,
          ext: ".jpg",
        }))}
      />
      <ModalRender params={currentModal} />
    </List>
  );
};
